import { useState } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Car,
  Coins,
  Cpu,
  FileText,
  Gem,
  Handshake,
  Landmark,
  Radio,
  Shield,
  Smartphone,
  Users,
  Wallet,
} from "lucide-react";
import {
  DIFFERENTIATORS,
  FUTURE_INTEGRATIONS,
  INDUSTRIES,
  INTEGRATIONS,
  JOURNEY,
  JOURNEY_PHASES,
} from "../../constants/verify";
import { CornerMarks, SectionShell, useFade } from "./ui";

const INDUSTRY_ICONS = {
  landmark: Landmark,
  building: Building2,
  wallet: Wallet,
  users: Users,
  coins: Coins,
  smartphone: Smartphone,
  shield: Shield,
  car: Car,
  handshake: Handshake,
  cpu: Cpu,
  radio: Radio,
  file: FileText,
  gem: Gem,
};

function JourneyBoard() {
  const [active, setActive] = useState(0);
  const step = JOURNEY[active];
  const last = JOURNEY.length - 1;
  const go = (index) => setActive(Math.max(0, Math.min(last, index)));

  const onTrackKeyDown = (event) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      go(active + 1);
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      go(active - 1);
    }
    if (event.key === "Home") {
      event.preventDefault();
      go(0);
    }
    if (event.key === "End") {
      event.preventDefault();
      go(last);
    }
  };

  return (
    <div className="mt-14">
      <div
        className="border border-navy/10"
        role="listbox"
        aria-label="Verification journey stations"
        tabIndex={0}
        onKeyDown={onTrackKeyDown}
      >
        {JOURNEY_PHASES.map((phase) => (
          <div
            key={phase.id}
            className="grid border-t border-navy/10 first:border-t-0 sm:grid-cols-3 lg:grid-cols-[9.5rem_repeat(3,minmax(0,1fr))]"
          >
            <div className="flex items-center gap-3 bg-verify-royal px-4 py-4 text-white sm:col-span-3 lg:col-span-1 lg:flex-col lg:items-start lg:justify-center lg:gap-2">
              <span className="font-mono text-[11px] tracking-[0.18em] text-verify-gold">{phase.n}</span>
              <div>
                <p className="text-sm font-semibold tracking-tight">{phase.title}</p>
                <p className="mt-1 hidden text-xs leading-snug text-white/75 lg:block">{phase.copy}</p>
              </div>
            </div>
            {phase.steps.map((index) => {
              const item = JOURNEY[index];
              const current = index === active;
              return (
                <button
                  key={item.n}
                  type="button"
                  role="option"
                  aria-selected={current}
                  onClick={() => go(index)}
                  onMouseEnter={() => go(index)}
                  className={`flex min-h-[6.25rem] flex-col items-start justify-between border-t border-navy/10 px-4 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-verify-gold sm:border-l lg:border-t-0 ${
                    current ? "bg-[#eef3fb]" : "bg-white hover:bg-[#eef3fb]/70"
                  }`}
                >
                  <span className={`font-mono text-[11px] tracking-[0.16em] ${current ? "text-verify-royal" : "text-navy/35"}`}>
                    {item.n}
                  </span>
                  <span className="mt-3 text-sm font-semibold leading-snug tracking-tight text-navy">
                    {item.title}
                  </span>
                  <span className={`mt-3 h-[2px] w-8 ${current ? "bg-verify-cyan" : "bg-transparent"}`} aria-hidden="true" />
                </button>
              );
            })}
          </div>
        ))}
        <div className="h-1 bg-[#eef3fb]" aria-hidden="true">
          <div
            className="h-full bg-verify-gold transition-[width] duration-300"
            style={{ width: `${((active + 1) / JOURNEY.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="relative mt-6 border border-navy/10 bg-white p-6 sm:p-8">
        <CornerMarks />
        <p className="font-mono text-[11px] tracking-[0.2em] text-verify-royal">
          STATION {step.n} / {String(JOURNEY.length).padStart(2, "0")}
        </p>
        <h3 className="mt-4 max-w-2xl text-2xl font-semibold tracking-tight text-navy sm:text-3xl">{step.title}</h3>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-navy/60">{step.copy}</p>
      </div>
    </div>
  );
}

function VerifySystem() {
  const { fade } = useFade();

  return (
    <>
      <SectionShell id="journey" labelledBy="journey-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              How SimVerify works
            </p>
            <h2
              id="journey-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              A smarter
              <span className="mt-1 block text-verify-royal">verification journey</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Nine stations in three movements — from the identifier you submit to the workflow you
              already run — with an explainable Trust Score at the handoff.
            </p>
          </motion.header>

          <JourneyBoard />

          <p className="mt-10 border-l-[3px] border-verify-gold pl-5 text-base leading-relaxed text-navy/70">
            This streamlined process enables faster, more accurate and more transparent verification
            decisions.
          </p>
        </div>
      </SectionShell>

      <SectionShell id="industries" labelledBy="industries-heading" tone="ice">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Industries we serve
            </p>
            <h2
              id="industries-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Digital trust across
              <span className="mt-1 block">every lending desk</span>
            </h2>
          </motion.header>

          <ul className="mt-12 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((item, index) => {
              const Icon = INDUSTRY_ICONS[item.icon] || Landmark;
              return (
                <li key={item.name} className="flex items-center gap-3 border-l-[3px] border-verify-royal bg-white px-3 py-3">
                  <span className="font-mono text-[11px] tracking-[0.14em] text-verify-gold">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <Icon className="h-4 w-4 shrink-0 text-verify-royal" strokeWidth={1.75} aria-hidden="true" />
                  <span className="text-sm font-semibold leading-snug tracking-tight text-navy">{item.name}</span>
                </li>
              );
            })}
          </ul>

          <div className="mt-16 grid overflow-hidden border border-navy/10 lg:grid-cols-2">
            <div className="bg-white px-6 py-8 sm:px-8 sm:py-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-verify-royal">
                Technology &amp; integrations
              </p>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight text-navy">
                Cloud-native, built to sit in your stack
              </h3>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-navy/60">
                SimVerify is built using modern cloud-native architecture with secure enterprise
                integrations.
              </p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {INTEGRATIONS.map((item) => (
                  <li key={item} className="border border-navy/10 bg-[#eef3fb] px-3 py-2 text-xs font-semibold text-navy/80">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-navy px-6 py-8 text-white sm:px-8 sm:py-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-verify-gold">
                Future integrations
              </p>
              <ul className="mt-6 space-y-3">
                {FUTURE_INTEGRATIONS.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-snug text-white/85">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-verify-cyan" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="choose" labelledBy="choose-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Why choose SimVerify?
            </p>
            <h2
              id="choose-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Most platforms answer one question.
              <span className="mt-1 block text-verify-royal">SimVerify answers the complete picture.</span>
            </h2>
          </motion.header>

          <div className="relative mt-12 border border-navy/10">
            <CornerMarks />
            <div className="grid items-stretch sm:grid-cols-2">
              <div className="flex flex-col justify-between border-b border-navy/10 bg-[#eef3fb] p-6 sm:border-b-0 sm:border-r sm:p-8">
                <p className="font-mono text-[11px] tracking-[0.2em] text-navy/40">Traditional</p>
                <h3 className="mt-6 text-xl font-semibold tracking-tight text-navy/55 sm:text-2xl">
                  One check. One file. One blind spot.
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-navy/50">
                  Credit history alone cannot see the asset, the device or the behaviour around the
                  SIM.
                </p>
              </div>
              <div className="flex flex-col justify-between bg-white p-6 sm:p-8">
                <p className="font-mono text-[11px] tracking-[0.2em] text-verify-royal">SimVerify</p>
                <h3 className="mt-6 text-xl font-semibold tracking-tight text-navy sm:text-2xl">
                  Borrower, asset, behaviour and score — together.
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-navy/60">
                  Unlike traditional verification systems, SimVerify combines seven intelligence
                  layers into one platform.
                </p>
              </div>
            </div>
            <ul className="grid auto-rows-fr gap-px border-t border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
              {DIFFERENTIATORS.map((item, index) => (
                <li
                  key={item}
                  className={`flex min-h-[8rem] flex-col bg-white p-5 ${index === DIFFERENTIATORS.length - 1 ? "lg:col-span-2" : ""}`}
                >
                  <span className="font-mono text-[11px] tracking-[0.16em] text-verify-royal">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-3 text-sm font-semibold leading-snug tracking-tight text-navy">{item}</p>
                </li>
              ))}
            </ul>
            <p className="border-t border-navy/10 bg-[#eef3fb] px-6 py-5 text-base font-semibold leading-snug text-navy sm:px-8">
              The result is better lending, lower fraud, healthier portfolios and greater confidence
              in every transaction.
            </p>
          </div>
        </div>
      </SectionShell>
    </>
  );
}

export default VerifySystem;
